import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, FileText, LayoutGrid, Trash2, ChevronDown, ChevronUp, Pencil, Save } from 'lucide-react'
import { useRewriteStore } from '@/stores/rewriteStore'
import { useAuthStore } from '@/stores/authStore'
import { rewriteApi } from '@/services/api'

const CHANNEL_ICONS: Record<string, typeof FileText> = {
  'LayoutGrid': LayoutGrid,
  'FileText': FileText,
}

const ICON_OPTIONS = [
  { value: 'FileText', label: '문서' },
  { value: 'LayoutGrid', label: '카드' },
]

interface Guideline {
  id: string
  title: string
  content: string
  type: string
  is_active: number
}

interface SectionDef {
  type: string
  title: string
  maxLength?: number
}

interface ChannelForm {
  name: string
  description: string
  icon: string
  tone: string
  rules: string
  sections: SectionDef[]
  // Structured tone fields
  speechLevel: string      // 존대/반말/혼합
  sentenceLength: string   // 짧게/보통/길게
  useEmoji: string         // 사용/미사용
  forbiddenWords: string   // 금칙어
  preferredExpressions: string // 선호 표현
}

const emptyForm: ChannelForm = {
  name: '', description: '', icon: 'FileText', tone: '', rules: '',
  sections: [{ type: 'body', title: '본문', maxLength: 0 }],
  speechLevel: '', sentenceLength: '', useEmoji: '', forbiddenWords: '', preferredExpressions: '',
}

export default function RewriteChannelsPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { channels, fetchChannels, createChannel, updateChannel } = useRewriteStore()
  const [expandedChannel, setExpandedChannel] = useState<string | null>(null)
  const [channelDetails, setChannelDetails] = useState<Record<string, { guidelines: Guideline[] }>>({})
  const [showAddGuideline, setShowAddGuideline] = useState<string | null>(null)
  const [guidelineForm, setGuidelineForm] = useState({ title: '', content: '', type: 'style' })

  // Channel create/edit
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null)
  const [form, setForm] = useState<ChannelForm>({ ...emptyForm })

  // Guideline inline edit
  const [editingGuidelineId, setEditingGuidelineId] = useState<string | null>(null)
  const [editGuidelineForm, setEditGuidelineForm] = useState({ title: '', content: '', type: 'style' })

  // Few-shot examples
  const [showAddExample, setShowAddExample] = useState<string | null>(null)
  const [exampleForm, setExampleForm] = useState({ title: '', source: '', result: '' })

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    fetchChannels()
  }, [isAuthenticated])

  const handleExpand = async (channelId: string) => {
    if (expandedChannel === channelId) {
      setExpandedChannel(null)
      return
    }
    setExpandedChannel(channelId)
    if (!channelDetails[channelId]) {
      const res = await rewriteApi.channels.get(channelId)
      if (res.success && res.data) {
        setChannelDetails((prev) => ({
          ...prev,
          [channelId]: { guidelines: (res.data as unknown as { guidelines: Guideline[] }).guidelines || [] },
        }))
      }
    }
  }

  const handleAddGuideline = async (channelId: string) => {
    if (!guidelineForm.title || !guidelineForm.content) return
    const res = await rewriteApi.channels.createGuideline({
      channelId,
      title: guidelineForm.title,
      content: guidelineForm.content,
      type: guidelineForm.type as 'style' | 'example' | 'constraint',
    })
    if (res.success) {
      const detailRes = await rewriteApi.channels.get(channelId)
      if (detailRes.success && detailRes.data) {
        setChannelDetails((prev) => ({
          ...prev,
          [channelId]: { guidelines: (detailRes.data as unknown as { guidelines: Guideline[] }).guidelines || [] },
        }))
      }
      setShowAddGuideline(null)
      setGuidelineForm({ title: '', content: '', type: 'style' })
    }
  }

  const handleDeleteGuideline = async (channelId: string, guidelineId: string) => {
    if (!confirm('가이드라인을 삭제하시겠습니까?')) return
    await rewriteApi.channels.deleteGuideline(guidelineId)
    setChannelDetails((prev) => ({
      ...prev,
      [channelId]: {
        guidelines: (prev[channelId]?.guidelines || []).filter((g) => g.id !== guidelineId),
      },
    }))
  }

  const handleUpdateGuideline = async (channelId: string, guidelineId: string) => {
    if (!editGuidelineForm.title || !editGuidelineForm.content) return
    const res = await rewriteApi.channels.updateGuideline(guidelineId, {
      title: editGuidelineForm.title,
      content: editGuidelineForm.content,
      type: editGuidelineForm.type,
    })
    if (res.success) {
      setChannelDetails((prev) => ({
        ...prev,
        [channelId]: {
          guidelines: (prev[channelId]?.guidelines || []).map((g) =>
            g.id === guidelineId ? { ...g, ...editGuidelineForm } : g
          ),
        },
      }))
      setEditingGuidelineId(null)
    }
  }

  const handleAddExample = async (channelId: string) => {
    if (!exampleForm.title || !exampleForm.source || !exampleForm.result) return
    const content = JSON.stringify({ source: exampleForm.source, result: exampleForm.result })
    const res = await rewriteApi.channels.createGuideline({
      channelId,
      title: exampleForm.title,
      content,
      type: 'example',
    })
    if (res.success) {
      const detailRes = await rewriteApi.channels.get(channelId)
      if (detailRes.success && detailRes.data) {
        setChannelDetails((prev) => ({
          ...prev,
          [channelId]: { guidelines: (detailRes.data as unknown as { guidelines: Guideline[] }).guidelines || [] },
        }))
      }
      setShowAddExample(null)
      setExampleForm({ title: '', source: '', result: '' })
    }
  }

  // Channel create
  const buildToneRules = () => {
    const hasBasic = form.tone.trim() || form.rules.trim()
    const hasStructured = form.speechLevel || form.sentenceLength || form.useEmoji || form.forbiddenWords.trim() || form.preferredExpressions.trim()
    if (!hasBasic && !hasStructured) return undefined
    return {
      tone: form.tone,
      rules: form.rules.split('\n').filter(Boolean),
      speechLevel: form.speechLevel || undefined,
      sentenceLength: form.sentenceLength || undefined,
      useEmoji: form.useEmoji || undefined,
      forbiddenWords: form.forbiddenWords.split('\n').filter(Boolean),
      preferredExpressions: form.preferredExpressions.split('\n').filter(Boolean),
    }
  }

  const handleCreateChannel = async () => {
    if (!form.name.trim()) return
    const toneRules = buildToneRules()
    const sections = form.sections.map((s) => ({
      type: s.type, title: s.title,
      ...(s.maxLength && s.maxLength > 0 ? { maxLength: s.maxLength } : {}),
    }))
    const result = await createChannel({
      name: form.name,
      description: form.description || undefined,
      icon: form.icon || undefined,
      structure: { sections },
      toneRules,
    })
    if (result) {
      setShowCreateForm(false)
      setForm({ ...emptyForm })
    }
  }

  // Channel edit start
  const startEditChannel = (channelId: string) => {
    const ch = channels.find((c) => c.id === channelId)
    if (!ch) return
    const structure = typeof ch.structure === 'string' ? JSON.parse(ch.structure) : ch.structure
    const toneRules = ch.tone_rules ? (typeof ch.tone_rules === 'string' ? JSON.parse(ch.tone_rules) : ch.tone_rules) : null
    setForm({
      name: ch.name,
      description: ch.description || '',
      icon: ch.icon || 'FileText',
      tone: toneRules?.tone || '',
      rules: (toneRules?.rules || []).join('\n'),
      sections: (structure?.sections || []).map((s: SectionDef) => ({
        type: s.type, title: s.title, maxLength: s.maxLength || 0,
      })),
      speechLevel: toneRules?.speechLevel || '',
      sentenceLength: toneRules?.sentenceLength || '',
      useEmoji: toneRules?.useEmoji || '',
      forbiddenWords: (toneRules?.forbiddenWords || []).join('\n'),
      preferredExpressions: (toneRules?.preferredExpressions || []).join('\n'),
    })
    setEditingChannelId(channelId)
    setShowCreateForm(true)
  }

  const handleUpdateChannel = async () => {
    if (!editingChannelId || !form.name.trim()) return
    const toneRules = buildToneRules()
    const sections = form.sections.map((s) => ({
      type: s.type, title: s.title,
      ...(s.maxLength && s.maxLength > 0 ? { maxLength: s.maxLength } : {}),
    }))
    await updateChannel(editingChannelId, {
      name: form.name,
      description: form.description || undefined,
      icon: form.icon || undefined,
      structure: JSON.stringify({ sections }),
      tone_rules: toneRules ? JSON.stringify(toneRules) : undefined,
    })
    setShowCreateForm(false)
    setEditingChannelId(null)
    setForm({ ...emptyForm })
  }

  const addSection = () => {
    setForm((f) => ({
      ...f,
      sections: [...f.sections, { type: 'body', title: '', maxLength: 0 }],
    }))
  }

  const removeSection = (idx: number) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.filter((_, i) => i !== idx),
    }))
  }

  const updateSection = (idx: number, field: keyof SectionDef, value: string | number) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }))
  }

  return (
    <div className="min-h-[calc(100vh-56px)] p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">채널 관리</h1>
          <p className="text-sm text-text-tertiary mt-1">콘텐츠 채널과 가이드라인을 관리합니다</p>
        </div>
        <button
          onClick={() => { setShowCreateForm(true); setEditingChannelId(null); setForm({ ...emptyForm }) }}
          className="btn btn-primary text-sm flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> 채널 추가
        </button>
      </div>

      {/* Channel Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-bg-secondary border border-border-subtle rounded-xl p-5 mb-6 space-y-4">
          <h3 className="font-semibold text-lg">{editingChannelId ? '채널 편집' : '새 채널 만들기'}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">채널 이름 <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="예: 블로그, 카드뉴스"
                className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">아이콘</label>
              <select
                value={form.icon}
                onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm"
              >
                {ICON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">설명</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="채널 설명"
              className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Tone & Manner Structured Editor */}
          <div className="space-y-3">
            <label className="block text-sm font-medium">톤앤매너</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">어투</label>
                <select
                  value={form.speechLevel}
                  onChange={(e) => setForm((f) => ({ ...f, speechLevel: e.target.value }))}
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">선택 안 함</option>
                  <option value="formal">존대말 (합니다체)</option>
                  <option value="polite">존대말 (해요체)</option>
                  <option value="casual">반말</option>
                  <option value="mixed">혼합</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">문장 길이</label>
                <select
                  value={form.sentenceLength}
                  onChange={(e) => setForm((f) => ({ ...f, sentenceLength: e.target.value }))}
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">선택 안 함</option>
                  <option value="short">짧게</option>
                  <option value="medium">보통</option>
                  <option value="long">길게</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">이모지 사용</label>
                <select
                  value={form.useEmoji}
                  onChange={(e) => setForm((f) => ({ ...f, useEmoji: e.target.value }))}
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">선택 안 함</option>
                  <option value="yes">사용</option>
                  <option value="no">미사용</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">톤 설명 (자유 입력)</label>
                <input
                  type="text"
                  value={form.tone}
                  onChange={(e) => setForm((f) => ({ ...f, tone: e.target.value }))}
                  placeholder="예: 친근하고 캐주얼한"
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">추가 규칙 (줄바꿈 구분)</label>
                <textarea
                  value={form.rules}
                  onChange={(e) => setForm((f) => ({ ...f, rules: e.target.value }))}
                  placeholder="한 줄에 하나씩 규칙 입력"
                  rows={2}
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">금칙어 (줄바꿈 구분)</label>
                <textarea
                  value={form.forbiddenWords}
                  onChange={(e) => setForm((f) => ({ ...f, forbiddenWords: e.target.value }))}
                  placeholder="사용하지 말아야 할 단어나 표현"
                  rows={2}
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">선호 표현 (줄바꿈 구분)</label>
                <textarea
                  value={form.preferredExpressions}
                  onChange={(e) => setForm((f) => ({ ...f, preferredExpressions: e.target.value }))}
                  placeholder="자주 사용할 표현이나 문구"
                  rows={2}
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
                />
              </div>
            </div>
          </div>

          {/* Sections */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">섹션 구조</label>
              <button onClick={addSection} className="text-xs text-primary hover:underline flex items-center gap-1">
                <Plus className="w-3 h-3" /> 섹션 추가
              </button>
            </div>
            <div className="space-y-2">
              {form.sections.map((section, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-bg-elevated rounded-lg px-3 py-2">
                  <input
                    type="text"
                    value={section.type}
                    onChange={(e) => updateSection(idx, 'type', e.target.value)}
                    placeholder="타입"
                    className="w-24 bg-bg-primary border border-border-subtle rounded px-2 py-1 text-xs"
                  />
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => updateSection(idx, 'title', e.target.value)}
                    placeholder="섹션 제목"
                    className="flex-1 bg-bg-primary border border-border-subtle rounded px-2 py-1 text-xs"
                  />
                  <input
                    type="number"
                    value={section.maxLength || ''}
                    onChange={(e) => updateSection(idx, 'maxLength', parseInt(e.target.value) || 0)}
                    placeholder="최대 글자수"
                    className="w-28 bg-bg-primary border border-border-subtle rounded px-2 py-1 text-xs"
                  />
                  {form.sections.length > 1 && (
                    <button onClick={() => removeSection(idx)} className="p-1 text-text-tertiary hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowCreateForm(false); setEditingChannelId(null) }} className="btn btn-ghost text-sm">취소</button>
            <button
              onClick={editingChannelId ? handleUpdateChannel : handleCreateChannel}
              className="btn btn-primary text-sm"
            >
              {editingChannelId ? '저장' : '채널 생성'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {channels.map((channel) => {
          const IconComponent = CHANNEL_ICONS[channel.icon || ''] || FileText
          const isExpanded = expandedChannel === channel.id
          const details = channelDetails[channel.id]
          const toneRules = channel.tone_rules ? (typeof channel.tone_rules === 'string' ? JSON.parse(channel.tone_rules) : channel.tone_rules) : null

          return (
            <div key={channel.id} className="bg-bg-secondary border border-border-subtle rounded-xl overflow-hidden">
              <button
                onClick={() => handleExpand(channel.id)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-bg-elevated transition-colors"
              >
                <div className="flex items-center gap-3">
                  <IconComponent className="w-5 h-5 text-primary" />
                  <div className="text-left">
                    <h3 className="font-semibold">{channel.name}</h3>
                    <p className="text-sm text-text-secondary">{channel.description}</p>
                  </div>
                  {channel.is_system ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-accent-gold/20 text-accent-gold">시스템</span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {!channel.is_system && (
                    <button
                      onClick={(e) => { e.stopPropagation(); startEditChannel(channel.id) }}
                      className="p-1.5 rounded hover:bg-bg-elevated text-text-tertiary hover:text-text-primary"
                      title="편집"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-text-tertiary" /> : <ChevronDown className="w-4 h-4 text-text-tertiary" />}
                </div>
              </button>

              {isExpanded && (
                <div className="px-5 pb-4 border-t border-border-subtle">
                  {/* Tone Rules */}
                  {toneRules && (
                    <div className="mt-3 mb-4">
                      <h4 className="text-sm font-medium mb-1">톤앤매너: {toneRules.tone}</h4>
                      <ul className="text-xs text-text-secondary space-y-0.5">
                        {(toneRules.rules as string[]).map((rule: string, i: number) => (
                          <li key={i}>- {rule}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Guidelines */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium">가이드라인</h4>
                      <button
                        onClick={() => setShowAddGuideline(showAddGuideline === channel.id ? null : channel.id)}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> 추가
                      </button>
                    </div>

                    {showAddGuideline === channel.id && (
                      <div className="bg-bg-elevated rounded-lg p-3 mb-3 space-y-2">
                        <input
                          type="text"
                          value={guidelineForm.title}
                          onChange={(e) => setGuidelineForm((f) => ({ ...f, title: e.target.value }))}
                          placeholder="가이드라인 제목"
                          className="w-full bg-bg-primary border border-border-subtle rounded px-3 py-1.5 text-sm"
                        />
                        <textarea
                          value={guidelineForm.content}
                          onChange={(e) => setGuidelineForm((f) => ({ ...f, content: e.target.value }))}
                          placeholder="가이드라인 내용"
                          rows={3}
                          className="w-full bg-bg-primary border border-border-subtle rounded px-3 py-1.5 text-sm resize-y"
                        />
                        <div className="flex items-center gap-2">
                          <select
                            value={guidelineForm.type}
                            onChange={(e) => setGuidelineForm((f) => ({ ...f, type: e.target.value }))}
                            className="bg-bg-primary border border-border-subtle rounded px-2 py-1 text-xs"
                          >
                            <option value="style">스타일</option>
                            <option value="example">예시</option>
                            <option value="constraint">제약조건</option>
                          </select>
                          <div className="flex-1" />
                          <button onClick={() => setShowAddGuideline(null)} className="btn btn-ghost text-xs">취소</button>
                          <button onClick={() => handleAddGuideline(channel.id)} className="btn btn-primary text-xs">저장</button>
                        </div>
                      </div>
                    )}

                    {details?.guidelines?.length ? (
                      <div className="space-y-2">
                        {details.guidelines.map((g) => (
                          <div key={g.id} className="bg-bg-elevated rounded-lg p-3">
                            {editingGuidelineId === g.id ? (
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  value={editGuidelineForm.title}
                                  onChange={(e) => setEditGuidelineForm((f) => ({ ...f, title: e.target.value }))}
                                  className="w-full bg-bg-primary border border-border-subtle rounded px-3 py-1.5 text-sm"
                                />
                                <textarea
                                  value={editGuidelineForm.content}
                                  onChange={(e) => setEditGuidelineForm((f) => ({ ...f, content: e.target.value }))}
                                  rows={3}
                                  className="w-full bg-bg-primary border border-border-subtle rounded px-3 py-1.5 text-sm resize-y"
                                />
                                <div className="flex items-center gap-2">
                                  <select
                                    value={editGuidelineForm.type}
                                    onChange={(e) => setEditGuidelineForm((f) => ({ ...f, type: e.target.value }))}
                                    className="bg-bg-primary border border-border-subtle rounded px-2 py-1 text-xs"
                                  >
                                    <option value="style">스타일</option>
                                    <option value="example">예시</option>
                                    <option value="constraint">제약조건</option>
                                  </select>
                                  <div className="flex-1" />
                                  <button onClick={() => setEditingGuidelineId(null)} className="btn btn-ghost text-xs">취소</button>
                                  <button onClick={() => handleUpdateGuideline(channel.id, g.id)} className="btn btn-primary text-xs flex items-center gap-1">
                                    <Save className="w-3 h-3" /> 저장
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">{g.type}</span>
                                    <span className="text-sm font-medium">{g.title}</span>
                                  </div>
                                  <p className="text-xs text-text-secondary mt-1 whitespace-pre-wrap">{g.content}</p>
                                </div>
                                <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                                  <button
                                    onClick={() => {
                                      setEditingGuidelineId(g.id)
                                      setEditGuidelineForm({ title: g.title, content: g.content, type: g.type })
                                    }}
                                    className="p-1 text-text-tertiary hover:text-primary"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteGuideline(channel.id, g.id)}
                                    className="p-1 text-text-tertiary hover:text-red-400"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-text-tertiary">가이드라인이 없습니다</p>
                    )}
                  </div>

                  {/* Few-shot Examples */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium">우수 사례 (Few-shot)</h4>
                      <button
                        onClick={() => setShowAddExample(showAddExample === channel.id ? null : channel.id)}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> 추가
                      </button>
                    </div>

                    {showAddExample === channel.id && (
                      <div className="bg-bg-elevated rounded-lg p-3 mb-3 space-y-2">
                        <input
                          type="text"
                          value={exampleForm.title}
                          onChange={(e) => setExampleForm((f) => ({ ...f, title: e.target.value }))}
                          placeholder="사례 제목"
                          className="w-full bg-bg-primary border border-border-subtle rounded px-3 py-1.5 text-sm"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-text-secondary mb-1">원천 소스</label>
                            <textarea
                              value={exampleForm.source}
                              onChange={(e) => setExampleForm((f) => ({ ...f, source: e.target.value }))}
                              placeholder="원본 텍스트"
                              rows={4}
                              className="w-full bg-bg-primary border border-border-subtle rounded px-3 py-1.5 text-sm resize-y"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-text-secondary mb-1">완성본</label>
                            <textarea
                              value={exampleForm.result}
                              onChange={(e) => setExampleForm((f) => ({ ...f, result: e.target.value }))}
                              placeholder="리라이팅된 완성 텍스트"
                              rows={4}
                              className="w-full bg-bg-primary border border-border-subtle rounded px-3 py-1.5 text-sm resize-y"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setShowAddExample(null)} className="btn btn-ghost text-xs">취소</button>
                          <button onClick={() => handleAddExample(channel.id)} className="btn btn-primary text-xs">저장</button>
                        </div>
                      </div>
                    )}

                    {(() => {
                      const examples = (details?.guidelines || []).filter(g => g.type === 'example')
                      if (examples.length === 0) return <p className="text-xs text-text-tertiary">우수 사례가 없습니다</p>
                      return (
                        <div className="space-y-2">
                          {examples.map((g) => {
                            let pair: { source?: string; result?: string } = {}
                            try { pair = JSON.parse(g.content) } catch { /* plain text */ }
                            return (
                              <div key={g.id} className="bg-bg-elevated rounded-lg p-3">
                                <div className="flex justify-between items-start mb-2">
                                  <span className="text-sm font-medium">{g.title}</span>
                                  <button
                                    onClick={() => handleDeleteGuideline(channel.id, g.id)}
                                    className="p-1 text-text-tertiary hover:text-red-400"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                {pair.source && pair.result ? (
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <span className="text-xs text-text-tertiary">원천</span>
                                      <p className="text-xs text-text-secondary mt-0.5 whitespace-pre-wrap line-clamp-4">{pair.source}</p>
                                    </div>
                                    <div>
                                      <span className="text-xs text-text-tertiary">완성</span>
                                      <p className="text-xs text-text-secondary mt-0.5 whitespace-pre-wrap line-clamp-4">{pair.result}</p>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-xs text-text-secondary whitespace-pre-wrap">{g.content}</p>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
